import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GLOBAL, POLICY_URL, deleteUrl, errorText, filterText } from '../../pages/SoftwarePolicy/policyEdit';
import {
  COMMON_LABELS, LIMIT_FIELDS, bodyOf, emptyForm, exceptionState, formOf, isGlobal, limitChips, ordered, patchPoliciesUrl, problemWith, todayText,
} from './patchPolicyLogic';

const list = (v) => (Array.isArray(v) ? v : []);
const when = (utc) => (utc ? String(utc).replace('T', ' ').slice(0, 16) : '');

// Add or change one patching policy: who it applies to (a filter of labels), the limits it sets, and for a customer an exception.
function PolicyDialog({ account, initial, onSave, onClose }) {
  const editing = Boolean(initial);
  const [form, setForm] = useState(() => (initial ? formOf(initial) : emptyForm(account)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLimit = (key, value) => setForm((f) => ({ ...f, limits: { ...f.limits, [key]: value } }));
  const setFilter = (i, patch) => setForm((f) => ({ ...f, filter: f.filter.map((row, n) => (n === i ? { ...row, ...patch } : row)) }));
  const problem = problemWith(form, account);
  const title = editing ? 'Change patching policy' : 'Add patching policy';

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(bodyOf(form, account));
    } catch (ex) {
      setError(errorText(ex));
      setSaving(false);
    }
  };

  return (
    <div className="v2-dialog-host">
      <div className="v2-dialog" role="dialog" aria-label={title} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <h2>{title}</h2>
        <p className="v2-muted" style={{ marginTop: 0 }}>
          {account === GLOBAL
            ? 'The standard for every customer. A customer can override any limit with its own policy.'
            : 'This customer\'s override. A limit set here wins over the global standard; a limit left blank comes from the global standard.'}
        </p>
        <label className="v2-field">Description
          <input className="v2-input" aria-label="Description" value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </label>

        <fieldset className="v2-field" style={{ border: 0, padding: 0 }}>
          <legend>Applies to machines where</legend>
          {form.filter.map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input className="v2-input" aria-label={`Filter label ${i + 1}`} list="patch-policy-labels" value={row.label} disabled={editing}
                onChange={(e) => setFilter(i, { label: e.target.value })} />
              <span style={{ alignSelf: 'center' }}>is</span>
              <input className="v2-input" aria-label={`Filter value ${i + 1}`} value={row.value} disabled={editing} onChange={(e) => setFilter(i, { value: e.target.value })} />
              {!editing && form.filter.length > 1 && (
                <button type="button" className="v2-btn" aria-label={`Remove filter ${i + 1}`} onClick={() => set({ filter: form.filter.filter((_, n) => n !== i) })}>Remove</button>
              )}
            </div>
          ))}
          <datalist id="patch-policy-labels">{COMMON_LABELS.map((l) => <option key={l} value={l} />)}</datalist>
          {!editing && <button type="button" className="v2-btn" onClick={() => set({ filter: [...form.filter, { label: '', value: '' }] })}>Add filter</button>}
          {editing && <div className="v2-sub">To change who this applies to, add a new policy and delete this one.</div>}
        </fieldset>

        <fieldset className="v2-field" style={{ border: 0, padding: 0 }}>
          <legend>Limits (leave blank to not set)</legend>
          {LIMIT_FIELDS.map((f) => (
            <div key={f.key} style={{ marginBottom: 8 }}>
              <label>{f.label}
                {f.flag ? (
                  <select className="v2-select" aria-label={f.label} value={form.limits[f.key] ?? ''} onChange={(e) => setLimit(f.key, e.target.value)}>
                    <option value="">Not set</option>
                    <option value="0">Not checked</option>
                    <option value="1">Flag it</option>
                  </select>
                ) : (
                  <input className="v2-input" aria-label={f.label} inputMode="numeric" value={form.limits[f.key] ?? ''} onChange={(e) => setLimit(f.key, e.target.value)} />
                )}
              </label>
              {f.labelKey && (
                <input className="v2-input" aria-label="Windows release name" placeholder="Name to show, for example Windows 11 24H2" value={form.featureLabel}
                  onChange={(e) => set({ featureLabel: e.target.value })} />
              )}
              <div className="v2-sub">{f.help}</div>
            </div>
          ))}
        </fieldset>

        {account !== GLOBAL && (
          <fieldset className="v2-field" style={{ border: 0, padding: 0 }}>
            <label><input type="checkbox" aria-label="This is an exception" checked={form.isException} onChange={(e) => set({ isException: e.target.checked })} /> This is an exception (a documented reason to loosen the standard)</label>
            {form.isException && (
              <>
                <label className="v2-field">Reason
                  <input className="v2-input" aria-label="Reason" value={form.exception.reason} onChange={(e) => set({ exception: { ...form.exception, reason: e.target.value } })} />
                </label>
                <label className="v2-field">Approved by
                  <input className="v2-input" aria-label="Approved by" value={form.exception.approvedBy} onChange={(e) => set({ exception: { ...form.exception, approvedBy: e.target.value } })} />
                </label>
                <label className="v2-field">Ends on
                  <input className="v2-input" type="date" aria-label="Ends on" value={form.exception.expires} onChange={(e) => set({ exception: { ...form.exception, expires: e.target.value } })} />
                </label>
                <div className="v2-sub">After this date the exception stops applying and the standard is back.</div>
              </>
            )}
          </fieldset>
        )}

        <label className="v2-field"><input type="checkbox" aria-label="Enabled" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Enabled</label>
        {problem && <div className="v2-sub" role="status">{problem}</div>}
        {error && <div className="v2-alert err" role="alert" style={{ marginTop: 10 }}>{error}</div>}
        <div className="actions">
          <button type="button" className="v2-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="v2-btn primary" onClick={save} disabled={saving || Boolean(problem)}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// The patching policies of one account (or, with account *, the global standard). A customer sees the global standard above its own
// overrides, read-only: it is changed on the Global policy page. onChanged runs after a save or delete (the report reads the limits again).
export default function PatchPolicySection({ account, onChanged }) {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const [state, setState] = useState({ state: 'idle', items: [] });
  const [editing, setEditing] = useState(null);       // null, 'new' or the policy being changed
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const today = todayText();
  const global = account === GLOBAL;

  const load = useCallback(async () => {
    if (!account) return;
    setState((s) => ({ ...s, state: 'loading' }));
    try {
      const r = await apiRef.current.request('GET', patchPoliciesUrl(account));
      setState({ state: 'ok', items: list(r.data?.patchPolicies) });
    } catch (ex) {
      setState({ state: 'error', error: errorText(ex), items: [] });
    }
  }, [account]);
  useEffect(() => { setEditing(null); setDeleting(null); setProblem(''); load(); }, [load]);

  const changed = async () => {
    await load();
    if (onChanged) onChanged();
  };
  const save = async (body) => {
    await apiRef.current.request('POST', POLICY_URL, body);
    setEditing(null);
    setProblem('');
    await changed();
  };
  const remove = async () => {
    setBusy(true);
    setProblem('');
    try {
      await apiRef.current.request('DELETE', deleteUrl(deleting.account, deleting.id));
      setDeleting(null);
      await changed();
    } catch (ex) {
      setProblem(errorText(ex));
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const rows = ordered(state.items);

  return (
    <section className="v2-card" aria-label="Patching policy">
      <div className="v2-bar">
        <h2 className="v2-h2">{global ? 'Patching standard' : 'Patching policy'}</h2>
        <button type="button" className="v2-btn primary" onClick={() => setEditing('new')}>Add patching policy</button>
      </div>
      <p className="v2-muted">
        {global
          ? 'How current Windows must be, for machines whose labels match. Every limit here can be overridden for one customer.'
          : 'The limits this customer\'s machines are judged by. Policies marked Global are the standard for everyone; a policy of the customer wins over them.'}
      </p>
      {problem && <div className="v2-alert err" role="alert">{problem}</div>}
      {state.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading patching policy" /></div>}
      {state.state === 'error' && <div className="v2-alert err">{state.error}</div>}
      {state.state === 'ok' && (rows.length === 0 ? <div className="v2-empty">No patching policies yet. The built-in limits apply.</div> : (
        <table className="v2-table" aria-label="Patching policies">
          <thead><tr><th>Applies to</th><th>Limits</th><th>Exception</th><th>Changed</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {rows.map((p) => {
              const fixed = isGlobal(p) && !global;               // the standard, seen from a customer: read only
              const exception = exceptionState(p.exception, today);
              return (
                <tr key={`${p.account}-${p.id}`}>
                  <td>
                    <div className="name" data-testid="policy-filter" style={{ fontWeight: 700 }}>{filterText(p.filter)}</div>
                    <div className="v2-sub">{[isGlobal(p) ? 'Global' : 'Customer', p.description, p.enabled === false ? 'switched off' : ''].filter(Boolean).join(' - ')}</div>
                  </td>
                  <td>{limitChips(p.patch).map((c) => <span key={c} className="v2-pill none" style={{ marginRight: 4 }}>{c}</span>)}</td>
                  <td>
                    {p.exception ? (
                      <>
                        <span className={`v2-pill ${exception === 'expired' ? 'err' : 'warn'}`}>{exception === 'expired' ? 'Expired' : 'Active'}</span>
                        <div className="v2-sub">{`${p.exception.reason} - ${p.exception.approvedBy}, until ${p.exception.expires}`}</div>
                      </>
                    ) : null}
                  </td>
                  <td>{[when(p.updatedUtc), p.updatedBy].filter(Boolean).join(' by ')}</td>
                  <td className="nowrap">
                    {fixed ? <span className="v2-sub">Change on Global policy</span> : (
                      <>
                        <button type="button" className="v2-btn" style={{ marginRight: 6 }} aria-label={`Change ${filterText(p.filter)}`} onClick={() => setEditing(p)}>Change</button>
                        <button type="button" className="v2-btn" aria-label={`Delete ${filterText(p.filter)}`} onClick={() => setDeleting(p)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ))}

      {editing && <PolicyDialog account={account} initial={editing === 'new' ? null : editing} onSave={save} onClose={() => setEditing(null)} />}
      {deleting && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label="Delete patching policy">
            <h2>{`Delete the patching policy for ${filterText(deleting.filter)}?`}</h2>
            <p>{global ? 'Every customer falls back to the built-in limits for these machines, unless it has its own policy.' : 'These machines fall back to the global standard.'}</p>
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setDeleting(null)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={remove} disabled={busy}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
